import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRefetchOnFocus } from './useRefetchOnFocus.js';
import { api } from '../lib/api.js';

export type OnboardingStep = 'business_description' | 'email' | 'connections' | 'contacts' | 'billing' | 'tour' | 'activation';
export type OnboardingStepStatus = 'pending' | 'completed' | 'skipped';

export interface OnboardingStepState {
  step: OnboardingStep;
  status: OnboardingStepStatus;
  completedAt: string | null;
}

export function isOnboardingComplete(progress: OnboardingStepState[] | undefined): boolean {
  if (!progress) return true; // sin datos todavía — nunca forzar la pantalla mientras carga

  // Pagar CIERRA la inducción (2026-08-19). 'activation' es el último paso por
  // diseño: cuando está hecho, el recorrido terminó.
  //
  // Sin esto, un cliente que ya pagó volvía a la pantalla de inducción en cada
  // login para siempre, porque 'connections' no se completa sola — cero
  // conexiones es un estado final válido, no "todavía no empezó" (ver el
  // comentario de hasRealData en services/onboarding.ts). Le estábamos diciendo
  // "te falta algo" a alguien que ya había terminado y pagado.
  const activation = progress.find(s => s.step === 'activation');
  if (activation && (activation.status === 'completed' || activation.status === 'skipped')) return true;

  return progress.every(s => s.status === 'completed' || s.status === 'skipped');
}

export function useOnboardingProgress(enabled = true) {
  return useQuery<{ progress: OnboardingStepState[]; redirectExempt?: boolean }>({
    queryKey: ['onboarding-progress'],
    queryFn: () => api.get<{ progress: OnboardingStepState[]; redirectExempt?: boolean }>('/api/onboarding/progress'),
    enabled,
    // Polling liviano mientras la inducción sigue abierta — los pasos avanzan
    // por chat con Evo Brain (tools) o por otra pestaña (Composio), no solo
    // por esta misma pantalla, así que un refetch pasivo importa acá más que
    // en el resto de la app.
    // 4s era el mecanismo PRINCIPAL de detección y generaba 225 peticiones cada
    // 15 min con la pestaña abierta sin tocar nada — casi la mitad del
    // presupuesto de rate limit por sí solo.
    //
    // Esta pantalla cambia por dos cosas: algo que hace el usuario (lo cubre la
    // invalidación de cada mutación) o volver de autorizar una conexión externa
    // (lo cubre el refresco por foco, abajo). El intervalo queda como RED para
    // lo que llegue de un tercero — 60s alcanza de sobra para eso.
    refetchInterval: (query) => (isOnboardingComplete(query.state.data?.progress) ? false : 60_000),
  });
}

/** Refresca el progreso al volver a la ventana — el caso real es volver de la
 *  pestaña de OAuth de Google/Instagram, donde el cambio ya ocurrió afuera. */
export function useOnboardingRefreshOnFocus(enabled = true): void {
  useRefetchOnFocus(['onboarding-progress'], enabled);
  // Las conexiones son el caso más claro: el usuario sale a autorizar en
  // Google/Instagram y vuelve. El cambio ya pasó afuera cuando vuelve el foco.
  useRefetchOnFocus(['onboarding-connections'], enabled);
}

export type ConnectableToolkit = 'instagram' | 'facebook' | 'googlecalendar';

export interface ConnectionStatus {
  toolkit: ConnectableToolkit;
  connected: boolean;
  connectedAt: string | null;
  /** Último intento que quedó a medias. null = nunca se intentó, o ya está
   *  conectado. El link de Composio dura 10 minutos y hasta hoy vencerse era
   *  indistinguible de no haber empezado nunca. */
  intentoVencidoAt: string | null;
}

export function useComposioConnections() {
  return useQuery<{ statuses: ConnectionStatus[] }>({
    queryKey: ['onboarding-connections'],
    queryFn: () => api.get<{ statuses: ConnectionStatus[] }>('/api/onboarding/connections'),
    refetchOnWindowFocus: true, // el cliente autoriza en OTRA pestaña y vuelve acá
  });
}

export function useStartComposioConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (toolkit: ConnectableToolkit) =>
      api.post<{ toolkit: string; redirectUrl: string; expiresAt: string }>(`/api/onboarding/connections/${toolkit}/start`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['onboarding-connections'] }),
  });
}

// ── De quién es cada conexión (2026-08-25) ───────────────────────────────────
// Endpoint APARTE del estado: aquel se pollea, y resolver la identidad cuesta
// una llamada a Composio por servicio. Acá se pide una vez y se deja quieto —
// el servidor además cachea 10 minutos.

export interface ConnectionIdentity {
  toolkit: string;
  connected: boolean;
  identidad: string | null;
}

export function useConnectionIdentities(enabled = true) {
  return useQuery<{ identities: ConnectionIdentity[] }>({
    queryKey: ['onboarding-connection-identities'],
    queryFn: () => api.get('/api/onboarding/connections/identities'),
    enabled,
    staleTime: 10 * 60_000,
    // No reintentar: si Composio no contesta, la pantalla muestra el estado
    // igual y la identidad queda en blanco. No vale bloquear por esto.
    retry: false,
  });
}

/** Borra la autorización. Irreversible: hay que volver a conectar desde cero. */
export function useDisconnectToolkit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (toolkit: string) =>
      api.delete<{ toolkit: string; borradas: string[]; canalesApagados: number }>(
        `/api/onboarding/connections/${toolkit}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['onboarding-connections'] });
      void qc.invalidateQueries({ queryKey: ['onboarding-connection-identities'] });
      void qc.invalidateQueries({ queryKey: ['messaging-channels'] });
      void qc.invalidateQueries({ queryKey: ['onboarding-progress'] });
    },
  });
}
